import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { AutoGrowTextarea } from '@/components/ui/auto-grow-textarea';
import { CADModelSelect } from '../../../cad/CADModelSelect';
import { DRBFMInputData } from '../../../../types';

interface BasicInfoCardProps {
  inputData: DRBFMInputData;
  onInputChange: (data: Partial<DRBFMInputData>) => void;
  onFieldChange: (
    field: keyof DRBFMInputData,
  ) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  isLoading: boolean;
  cadEnabled?: boolean | null;
  partLabelsJson: string;
  onPartLabelsChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  partLabelsError: string | null;
  validationErrors: Record<string, string>;
}

const BasicInfoCard: React.FC<BasicInfoCardProps> = ({
  inputData,
  onInputChange,
  onFieldChange,
  isLoading,
  cadEnabled,
  partLabelsJson,
  onPartLabelsChange,
  partLabelsError,
  validationErrors,
}) => {
  const { t } = useTranslation();
  const partLabelsMessage = partLabelsError || validationErrors.predefinedPartLabels;

  return (
    <Card>
      <CardContent className="p-4 pb-6">
        <p className="revy-cjk mb-2 flex items-center gap-2 text-base leading-[1.75]">
          <FileText className="text-primary" />
          {t('drbfm.input.basicInfo')}
        </p>
        <Separator className="mb-4" />

        {/* MUI's md is 900px, Tailwind's is 768 — the arbitrary query keeps the
            breakpoint where it was. */}
        <div className="grid grid-cols-1 gap-4 min-[900px]:grid-cols-2">
          <Field
            label={t('drbfm.input.itemName')}
            required
            error={validationErrors.itemName}
            help={validationErrors.itemName ? undefined : t('drbfm.input.itemNameHelper')}
          >
            {(field) => (
              <Input
                {...field}
                value={inputData.itemName}
                onChange={onFieldChange('itemName')}
                placeholder={t('drbfm.input.itemNamePlaceholder')}
                required
              />
            )}
          </Field>

          <Field
            label={t('drbfm.input.analysisTitle')}
            error={validationErrors.title}
            help={validationErrors.title ? undefined : t('drbfm.input.analysisTitleHelper')}
          >
            {(field) => (
              <Input
                {...field}
                value={inputData.title}
                onChange={onFieldChange('title')}
                placeholder={t('drbfm.input.analysisTitlePlaceholder')}
              />
            )}
          </Field>

          {cadEnabled === true && (
            <div className="min-[900px]:col-span-2">
              <CADModelSelect
                value={inputData.cadModelId || ''}
                onChange={(cadModelId) => onInputChange({ cadModelId })}
                disabled={isLoading}
                helperText={t('drbfm.cadLink.inputHelper', 'CADモデルを選ぶと、DRBFM解析完了後に3D上の該当部位へ自動リンクします')}
              />
            </div>
          )}

          {cadEnabled === true && inputData.cadModelId && (
            <div className="min-[900px]:col-span-2">
              <Field
                label={t('drbfm.cadLink.partLabelsJson', '部位ラベルJSON')}
                error={partLabelsMessage}
                help={partLabelsMessage ? undefined : t('drbfm.cadLink.partLabelsHelper', 'cover_part_labels.json全体、またはparts配列を貼り付けると優先ラベルとして使います')}
              >
                {(field) => (
                  <AutoGrowTextarea
                    {...field}
                    value={partLabelsJson}
                    onChange={onPartLabelsChange}
                    disabled={isLoading}
                    rows={3}
                  />
                )}
              </Field>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BasicInfoCard;
